/**
 * Cache du cockpit « Suivi des exigences » (INSTR-017) — endpoint + invalidation.
 *
 * Pourquoi un module à part : les LIGNES du cockpit sont servies par un
 * `<DataTable mode="server">` dont la clé de cache est `[MATRIX_ENDPOINT, params]`.
 * React Query compare les éléments du tableau UN À UN : invalider
 * `['/api/instructors']` ne touche PAS `['/api/instructors/requirements-matrix/list', …]`
 * — ce ne sont pas des préfixes l'un de l'autre. Les mutations qui font bouger
 * une ligne du cockpit (validation d'exigence, dépôt validé depuis la file,
 * conformité sous-traitance, changement de nature) vivent dans quatre hooks
 * différents ; chacun avait sa propre paire d'`invalidateQueries` bricolée et
 * aucune ne visait la bonne clé. Résultat vu à l'écran : l'en-tête passait à
 * « 246 appellent une action » pendant que la ligne juste en dessous restait
 * « Manquante », et l'utilisateur revalidait une seconde fois.
 *
 * UNE fonction, N appelants : tout ce qui écrit une donnée affichée par le
 * cockpit appelle `invalidateRequirementsMatrix`. `invalidateDataTableListings`
 * couvre le listing, les compteurs de groupes ET les facettes du panneau de
 * filtres — les trois clés que le cockpit utilise.
 */

import type { QueryClient } from '@tanstack/react-query'

import { invalidateDataTableListings } from '@/lib/datatable-invalidate'
import { queryKeys } from '@/lib/queryKeys'

/** Endpoint du listing serveur du cockpit — clé de cache du DataTable. */
export const MATRIX_ENDPOINT = '/api/instructors/requirements-matrix/list'

/**
 * Rafraîchit TOUT ce que le cockpit affiche : le récapitulatif d'en-tête
 * (`queryKeys.instructors.requirementsMatrix()`), les lignes, les compteurs de
 * regroupement et les facettes.
 *
 * ⚠️ `refetchType: 'all'` (posé par `invalidateDataTableListings`) est requis :
 * la file de validation est montée SUR la page du cockpit, le tableau reste donc
 * monté derrière, et avec `staleTime: 30_000` + `refetchOnWindowFocus: false`
 * (main.tsx) il ne se relirait jamais tout seul.
 */
export function invalidateRequirementsMatrix(qc: QueryClient): void {
    invalidateDataTableListings(qc, MATRIX_ENDPOINT, queryKeys.instructors.requirementsMatrix())
}
